import React, { useEffect, useRef, useState } from "react";
import axios from "../../../utills/api";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Container,
} from "reactstrap";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import TableContainer from "../../../Components/Common/TableContainer";
import BreadCrumb from "../../../Components/Common/BreadCrumb"
import {
  FaCheck,
  FaTimes,
  FaSmoking,
  FaCrown,
  FaLink,
  FaEdit,
  FaTrash,
} from "react-icons/fa";

import RoomQRCode from "./RoomQRCode";

/* =========================
   CONFIG
========================= */
const API_ROOM = "manage";

/* =========================
   IMAGE CAROUSEL
========================= */
const SimpleCarousel = ({ images = [] }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => setIndex(0), [images]);

  if (!images.length) return null;

  return (
    <div style={{ position: "relative" }}>
      <div style={{ height: 200, overflow: "hidden", borderRadius: 8 }}>
        <img
          src={images[index]}
          alt="room"
          style={{ width: "100%", height: 200, objectFit: "cover" }}
        />
      </div>

      <button
        onClick={() =>
          setIndex((i) => (i === 0 ? images.length - 1 : i - 1))
        }
        style={btnStyle("left")}
      >
        ‹
      </button>

      <button
        onClick={() =>
          setIndex((i) => (i === images.length - 1 ? 0 : i + 1))
        }
        style={btnStyle("right")}
      >
        ›
      </button>
    </div>
  );
};

const btnStyle = (side) => ({
  position: "absolute",
  top: "50%",
  [side]: 8,
  transform: "translateY(-50%)",
  background: "rgba(0,0,0,0.5)",
  color: "#fff",
  border: "none",
  borderRadius: "50%",
  width: 36,
  height: 36,
});

/* =========================
   MAIN COMPONENT
========================= */
const ManageRooms = () => {
  document.title = "HMS | Rooms";

  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);

  const [search, setSearch] = useState("");
  const [roomTypeFilter, setRoomTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const createdBlobUrls = useRef([]);

  const [form, setForm] = useState({
    roomNumber: "",
    roomTypeId: "",
    floor: "",
    housekeepingStatus: "CLEAN",
    smoking: false,
    notes: "",
  });


  /* =========================
     LOAD DATA
  ========================= */
  const loadRooms = async () => {
    try {
      const [
        roomsRes,
        tracerRes,
      ] = await Promise.all([
        axios.get(
          `${API_ROOM}/add-rooms`
        ),

        axios.get(
          `/bookings/room-calendar`
        ),
      ]);

      const rooms =
        roomsRes.data || [];

      const tracerMap =
        tracerRes.data
          ?.tracer || {};

      const finalRooms =
        rooms.map((room) => ({
          ...room,

          tracer:
            tracerMap[
            room._id
            ] || [],
        }));

      setRooms(
        finalRooms
      );

      if (
        !selectedRoom &&
        finalRooms.length
      ) {
        setSelectedRoom(
          finalRooms[0]
        );
      }
    } catch (err) {
      console.log(err);

      toast.error(
        "Failed to load rooms"
      );
    }
  };

  const loadRoomTypes = async () => {
    try {
      const res = await axios.get(`${API_ROOM}/room-types`);
      setRoomTypes(res.data || []);
    } catch {
      toast.error("Failed to load room types");
    }
  };

  useEffect(() => {
    loadRooms();
    loadRoomTypes();
  }, []);

  /* =========================
     FORM HANDLER
  ========================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* =========================
     ROOM TYPE CHANGE
  ========================= */
  const handleRoomTypeChange = (e) => {
    const roomTypeId = e.target.value;

    setForm((p) => ({
      ...p,
      roomTypeId,
    }));
  };

  /* =========================
     CREATE / UPDATE
  ========================= */
  const handleSubmit = async () => {
    if (!form.roomTypeId || !form.roomNumber) {
      toast.error("Room Type & Room Number required");
      return;
    }

    try {
      if (editingRoom) {
        await axios.put(
          `${API_ROOM}/add-rooms/${editingRoom._id}`,
          form
        );
        toast.success("Room updated");
      } else {
        await axios.post(`${API_ROOM}/add-rooms`, form);
        toast.success("Room created");
      }

      setModalOpen(false);
      setEditingRoom(null);
      setForm({
        roomNumber: "",
        roomTypeId: "",
        floor: "",
        housekeepingStatus: "CLEAN",
        smoking: false,
        notes: "",
      });

      loadRooms();
    } catch {
      toast.error("Operation failed");
    }
  };

  /* =========================
     EDIT
  ========================= */
  const handleEdit = (room) => {
    setEditingRoom(room);
    setForm({
      roomNumber: room.roomNumber,
      roomTypeId: room.roomType?._id,
      floor: room.floor,
      housekeepingStatus: room.housekeepingStatus,
      smoking: room.smoking,
      notes: room.notes,
    });
    setModalOpen(true);
  };

  /* =========================
     DELETE
  ========================= */
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_ROOM}/add-rooms/${id}`);
      setRooms((p) => p.filter((r) => r._id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  /* =========================
     FILTER
  ========================= */
  const filtered = rooms.filter((room) => {
    const searchMatch =
      room.roomNumber
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      room.roomType?.name
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      room.building
        ?.toLowerCase()
        .includes(search.toLowerCase());

    const roomTypeMatch =
      roomTypeFilter === "ALL" ||
      room.roomType?.name === roomTypeFilter;

    let statusMatch = true;

    if (statusFilter !== "ALL") {
      switch (statusFilter) {
        case "VACANT":
        case "RESERVED":
        case "OCCUPIED":
          statusMatch =
            room.occupancyStatus === statusFilter;
          break;

        case "CLEAN":
        case "DIRTY":
        case "INSPECTED":
        case "CLEANING":
          statusMatch =
            room.housekeepingStatus === statusFilter;
          break;

        case "ACTIVE":
        case "BLOCKED":
        case "MAINTENANCE":
        case "OUT_OF_ORDER":
          statusMatch =
            room.operationalStatus === statusFilter;
          break;

        case "SMOKING":
          statusMatch = room.smoking === true;
          break;

        case "NON_SMOKING":
          statusMatch = room.smoking === false;
          break;

        case "CONNECTING":
          statusMatch =
            room.isConnectingRoom === true;
          break;

        case "VIP":
          statusMatch =
            room.isVipRoom === true;
          break;

        default:
          statusMatch = true;
      }
    }

    return (
      searchMatch &&
      roomTypeMatch &&
      statusMatch
    );
  });

  const getBadgeStyle = (color) => ({
    background: "#fff",
    color,
    border: `1px solid ${color}`,
    borderRadius: "4px",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
  });

  const statusColor = {
    VACANT: "#28a745",
    RESERVED: "#0d6efd",
    OCCUPIED: "#dc3545",

    CLEAN: "#28a745",
    DIRTY: "#fd7e14",
    INSPECTED: "#20c997",
    CLEANING: "#17a2b8",

    ACTIVE: "#28a745",
    BLOCKED: "#dc3545",
    MAINTENANCE: "#ffc107",
    OUT_OF_ORDER: "#6c757d",
  };

  const roomTypeOptions = [
    "ALL",
    ...new Set(
      rooms
        .map((r) => r.roomType?.name)
        .filter(Boolean)
    ),
  ];

  const statusOptions = [
    "ALL",

    // Occupancy
    "VACANT",
    "RESERVED",
    "OCCUPIED",

    // Housekeeping
    "CLEAN",
    "DIRTY",
    "INSPECTED",
    "CLEANING",

    // Operational
    "ACTIVE",
    "BLOCKED",
    "MAINTENANCE",
    "OUT_OF_ORDER",

    // Features
    "SMOKING",
    "NON_SMOKING",
    "CONNECTING",
    "VIP",
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Rooms" pageTitle="Hotel" />
        {/* 🔥 TOP BAR */}
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

              <div className="d-flex gap-2 flex-wrap">

                <Input
                  placeholder="Search room..."
                  style={{ width: "250px" }}
                  value={search}
                  onChange={(e) =>
                    setSearch(e.target.value)
                  }
                />

                <Input
                  type="select"
                  style={{ width: "220px" }}
                  value={roomTypeFilter}
                  onChange={(e) =>
                    setRoomTypeFilter(e.target.value)
                  }
                >
                  {roomTypeOptions.map((type) => (
                    <option
                      key={type}
                      value={type}
                    >
                      {type === "ALL"
                        ? "All Room Types"
                        : type}
                    </option>
                  ))}
                </Input>

                <Input
                  type="select"
                  style={{ width: "220px" }}
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value)
                  }
                >
                  {statusOptions.map((status) => (
                    <option
                      key={status}
                      value={status}
                    >
                      {status === "ALL"
                        ? "All Status"
                        : status
                          .replaceAll("_", " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (l) =>
                            l.toUpperCase()
                          )}
                    </option>
                  ))}
                </Input>

              </div>

              <Button
                color="primary"
                onClick={() => setModalOpen(true)}
              >
                + Add Room
              </Button>

            </div>
          </CardBody>
        </Card>

        <Row>
          <Card>
            <CardBody>
              <CardBody>
                <Row>
                  {filtered.map((room) => (
                    <Col lg={4} md={6} sm={12} key={room._id} className="mb-4">
                      <Card
                        className="h-100 border-1"
                        style={{
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <CardBody>
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h5 className="mb-1 fw-bold">
                                {room.roomType?.name || "N/A"}
                              </h5>

                              <small className="text-muted">
                                Room #{room.roomNumber}
                              </small>
                            </div>
                            <div className="d-flex gap-2">

                              <RoomQRCode room={room} />

                              <button
                                className="btn btn-sm btn-soft-primary"
                                onClick={() => handleEdit(room)}
                              >
                                <i className="ri-edit-line"></i>
                              </button>

                              <button
                                className="btn btn-sm btn-soft-danger"
                                onClick={() => handleDelete(room._id)}
                              >
                                <i className="las la-trash-alt"></i>
                              </button>

                            </div>
                          </div>



                          {/* Building / Floor / Wing */}
                          <Row className="mb-3 text-center">
                            <Col xs={4}>
                              <div className="text-muted small">Building</div>
                              <div className="fw-semibold">
                                {room.building || "-"}
                              </div>
                            </Col>

                            <Col xs={4}>
                              <div className="text-muted small">Floor</div>
                              <div className="fw-semibold">
                                {room.floor || "-"}
                              </div>
                            </Col>

                            <Col xs={4}>
                              <div className="text-muted small">Wing</div>
                              <div className="fw-semibold">
                                {room.wing || "-"}
                              </div>
                            </Col>
                          </Row>

                          {/* Status Row */}
                          <Row className="text-center mb-3" style={{ fontSize: "12px" }}>

                            <Col xs={4}>
                              <span
                                style={getBadgeStyle(
                                  statusColor[room.occupancyStatus]
                                )}
                              >
                                {room.occupancyStatus}
                              </span>
                              <div className="small text-muted">
                                Occupancy
                              </div>
                            </Col>

                            <Col xs={4}>


                              <span
                                style={getBadgeStyle(
                                  statusColor[room.housekeepingStatus]
                                )}
                              >
                                {room.housekeepingStatus}
                              </span>
                              <div className="small text-muted">
                                Housekeeping
                              </div>
                            </Col>

                            <Col xs={4}>


                              <span
                                style={getBadgeStyle(
                                  statusColor[room.operationalStatus]
                                )}
                              >
                                {room.operationalStatus}
                              </span>
                              <div className="small text-muted">
                                Operational
                              </div>
                            </Col>

                          </Row>

                          {/* Notes */}
                          {room.notes && (
                            <div
                              className="mb-2 p-2"
                              style={{
                                background: "#f8f9fa",
                                borderRadius: "10px",
                              }}
                            >
                              <div
                                className="small text-muted mb-1"
                                style={{ fontWeight: 600 }}
                              >
                                Notes
                              </div>

                              <div style={{ fontSize: "13px" }}>
                                {room.notes}
                              </div>
                            </div>
                          )}

                          {/* Room Features */}
                          <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">

                            {/* Smoking */}
                            <span
                              style={getBadgeStyle(
                                room.smoking ? "#28a745" : "#dc3545"
                              )}
                            >
                              {room.smoking ? (
                                <>
                                  <FaSmoking className="me-1" />
                                  Smoking
                                </>
                              ) : (
                                <>
                                  <FaTimes className="me-1" />
                                  Non Smoking
                                </>
                              )}
                            </span>

                            {/* VIP */}
                            {room.isVipRoom && (
                              <span style={getBadgeStyle("#ffc107")}>
                                <FaCrown className="me-1" />
                                VIP Room
                              </span>
                            )}

                            {/* Connecting Room */}
                            {room.isConnectingRoom && (
                              <span style={getBadgeStyle("#0d6efd")}>
                                <FaLink className="me-1" />
                                Connecting
                              </span>
                            )}

                          </div>

                          {/* Maintenance Reason */}
                          {room.operationalStatus === "MAINTENANCE" &&
                            room.maintenanceReason && (
                              <div
                                className="mt-2"
                                style={{
                                  fontSize: "12px",
                                  color: "#dc3545",
                                }}
                              >
                                <strong>Maintenance Reason:</strong>{" "}
                                {room.maintenanceReason}
                              </div>
                            )}

                          {/* weekly tracer */}

                          <div className="d-flex justify-content-center gap-1 mt-2 mb-3">
                            {(room.tracer || []).map(
                              (d, i) => (
                                <div
                                  key={i}
                                  title={`${d.date} - ${d.status}`}
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                      "center",
                                    color: "#fff",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    backgroundColor:
                                      d.status ===
                                        "OCCUPIED"
                                        ? "#dc3545"
                                        : d.status ===
                                          "MAINTENANCE"
                                          ? "#ffc107"
                                          : "#28a745",
                                  }}
                                >
                                  {d.day}
                                </div>
                              )
                            )}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </CardBody>
          </Card>

        </Row>

        {/* ================= MODAL ================= */}
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
          <ModalHeader>
            {editingRoom ? "Edit Room" : "Add Room"}
          </ModalHeader>

          <ModalBody>
            <Form>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Room Type</Label>
                    <Input
                      type="select"
                      name="roomTypeId"
                      value={form.roomTypeId}
                      onChange={handleRoomTypeChange}
                    >
                      <option value="">Select</option>
                      {roomTypes.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label>Room Number</Label>
                    <Input
                      type="text"
                      name="roomNumber"
                      value={form.roomNumber}
                      onChange={handleChange}
                      placeholder="Enter Room Number (101, A-201, Deluxe-01)"
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label>Floor</Label>
                    <Input
                      name="floor"
                      value={form.floor}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label>Building</Label>
                    <Input
                      name="building"
                      value={form.building}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup>
                    <Label>Wing</Label>
                    <Input
                      name="wing"
                      value={form.wing}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>


                <Col md={6}>
                  <FormGroup>
                    <Label>Operational Status</Label>
                    <Input
                      type="select"
                      name="operationalStatus"
                      value={form.operationalStatus}
                      onChange={handleChange}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="BLOCKED">BLOCKED</option>
                      <option value="OUT_OF_ORDER">OUT_OF_ORDER</option>
                      <option value="MAINTENANCE">MAINTENANCE</option>
                    </Input>
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup check className="mt-4">
                    <Input
                      type="checkbox"
                      name="smoking"
                      checked={form.smoking}
                      onChange={handleChange}
                    />
                    <Label check className="ms-2">
                      Smoking Allowed
                    </Label>
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup check className="mt-4">
                    <Input
                      type="checkbox"
                      name="isVipRoom"
                      checked={form.isVipRoom}
                      onChange={handleChange}
                    />
                    <Label check className="ms-2">
                      VIP Room
                    </Label>
                  </FormGroup>
                </Col>

                <Col md={6}>
                  <FormGroup check className="mt-2">
                    <Input
                      type="checkbox"
                      name="isConnectingRoom"
                      checked={form.isConnectingRoom}
                      onChange={handleChange}
                    />
                    <Label check className="ms-2">
                      Connecting Room
                    </Label>
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label>Maintenance Reason</Label>
                    <Input
                      type="textarea"
                      name="maintenanceReason"
                      value={form.maintenanceReason}
                      onChange={handleChange}
                      rows={3}
                    />
                  </FormGroup>
                </Col>

                <Col md={12}>
                  <FormGroup>
                    <Label>Notes</Label>
                    <Input
                      type="textarea"
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      rows={3}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </Form>
          </ModalBody>

          <ModalFooter>
            <Button color="primary" onClick={handleSubmit}>
              {editingRoom ? "Update" : "Create"}
            </Button>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default ManageRooms;